import { Box, FormHelperText, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { usePayment, useTheme } from "../hooks";
import { PaymentMethod } from "../models/payment";
import { CheckCircle } from "@mui/icons-material";

const PaymentMethods = ({
  payments = [],
  showTitle = true,
  error = "",
}: {
  payments: PaymentMethod[];
  // Off where a titled group around the tiles already names them.
  showTitle?: boolean;
  // The step's refusal when Continue is pressed with no method chosen.
  error?: string;
}) => {
  const { selectedMethod, setSelectedMethod } = usePayment();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const primary = theme?.color_primary || grey[900];

  return (
    <Box mb={showTitle ? 4 : 2}>
      {showTitle && (
        <Typography sx={{ fontSize: 14, fontWeight: 600 }} mb={1.5}>
          {`${t("payment.select_method")}*`}
        </Typography>
      )}

      {/* Tiles wrap rather than divide the row, so a narrow column never
          squeezes a tile below its own logo and name. */}
      <Box
        role="radiogroup"
        aria-label={t("payment.form.method_title")}
        sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}
      >
        {payments
          .filter(method => method?.is_active)
          .map((method: PaymentMethod) => {
            const isSelected = selectedMethod?.id === method.id;

            return (
              <Box
                key={method?.id}
                role="radio"
                aria-checked={isSelected}
                aria-label={method.name}
                tabIndex={0}
                onClick={() => setSelectedMethod(method)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedMethod(method);
                  }
                }}
                sx={{
                  flex: "1 1 200px",
                  maxWidth: { sm: "calc(50% - 6px)" },
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  boxSizing: "border-box",
                  borderRadius: 3,
                  border: 1,
                  borderColor: isSelected
                    ? primary
                    : error
                    ? "error.main"
                    : "divider",
                  // The selected tile keeps a hairline border and gains an
                  // inner ring, so choosing a method does not move the layout.
                  boxShadow: isSelected ? `inset 0 0 0 1px ${primary}` : "none",
                  bgcolor: "background.paper",
                  cursor: "pointer",
                  "&:focus-visible": { outline: `2px solid ${primary}` },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: "10px",
                    overflow: "hidden",
                    // A light tile, so a logo that is still loading (or never
                    // loads) leaves an empty tile, not a dark grey block.
                    bgcolor: "grey.100",
                  }}
                >
                  <img
                    src={method.logo.url}
                    alt={t("payment.provider_logo_alt")}
                    onError={event => {
                      event.currentTarget.style.visibility = "hidden";
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                {/* The name stays visible when selected; the tick joins it. */}
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 15,
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {method.name}
                </Typography>
                {isSelected ? (
                  <CheckCircle
                    aria-hidden
                    sx={{ fontSize: 24, color: primary, flexShrink: 0 }}
                  />
                ) : (
                  <Box
                    aria-hidden
                    sx={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      borderRadius: "50%",
                      border: 2,
                      borderColor: grey[400],
                    }}
                  />
                )}
              </Box>
            );
          })}
      </Box>
      {error && (
        <FormHelperText error role="alert" sx={{ mx: 0 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default PaymentMethods;
